import React from 'react';
import { getSocket } from '@/lib/socket';
import { useRoomStore } from '@/stores/roomStore';
import { Button } from '@/components/Button';
import { useToastStore } from '@/components/Toast';

interface QueueProps {
  roomId: string;
}

export const Queue: React.FC<QueueProps> = () => {
  const socket = getSocket();
  const { playlist, currentIndex, myRole } = useRoomStore();
  const addToast = useToastStore((state) => state.addToast);

  const canManage = myRole === 'owner' || myRole === 'dj';

  const handleRemove = (index: number) => {
    if (!canManage) return;

    socket.emit('playlist:remove', { index }, (response: { success: boolean; error?: string }) => {
      if (response.success) {
        addToast('Track removed from queue', 'success');
      } else {
        addToast(response.error || 'Failed to remove track', 'error');
      }
    });
  };

  const handleJump = (index: number) => {
    if (!canManage) return;

    socket.emit('playlist:jump', { index }, (response: { success: boolean; error?: string }) => {
      if (response.success) {
        addToast('Jumped to track', 'success');
      } else {
        addToast(response.error || 'Failed to jump to track', 'error');
      }
    });
  };

  const extractVideoId = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('v') || url;
    } catch {
      return url;
    }
  };

  const getThumbnail = (videoId: string) => {
    const cleanId = extractVideoId(videoId);
    return `https://img.youtube.com/vi/${cleanId}/mqdefault.jpg`;
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-primary">
          Queue ({playlist.length})
        </h2>
      </div>

      {playlist.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-tertiary mb-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.5 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" clipRule="evenodd" />
          </svg>
          <p className="text-secondary font-bold">Queue is empty</p>
          <p className="text-sm text-tertiary mt-1">Add songs to get started</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {playlist.map((track, index) => {
            const isCurrentTrack = index === currentIndex;

            return (
              <div
                key={track.id}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  isCurrentTrack
                    ? 'bg-lime-400/10 border-2 border-lime-400'
                    : 'bg-muted hover:opacity-80 border-2 border-transparent'
                }`}
              >
                {/* Track Number / Playing Indicator */}
                <div className="flex-shrink-0 w-8 text-center">
                  {isCurrentTrack ? (
                    <div className="flex items-center justify-center">
                      <svg className="w-5 h-5 text-lime-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : (
                    <span className="text-sm font-bold text-tertiary">{index + 1}</span>
                  )}
                </div>

                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  <img
                    src={getThumbnail(track.videoId)}
                    alt={track.title}
                    className="w-16 h-12 object-cover rounded-md"
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="90"%3E%3Crect fill="%233d4654" width="120" height="90"/%3E%3C/svg%3E';
                    }}
                  />
                </div>

                {/* Track Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-primary truncate">{track.title}</p>
                  <p className="text-xs text-tertiary">
                    Added by {track.addedBy} • {new Date(track.addedAt).toLocaleTimeString()}
                  </p>
                </div>

                {/* Actions */}
                {canManage && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!isCurrentTrack && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleJump(index)}
                        title="Play this track"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(index)}
                      title="Remove from queue"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
